import { states, countries } from './parameters';

// Common location mappings for quick lookup
const commonLocations = {
    '316': { name: 'United States', type: 'country' },
    '557': { name: 'Ohio, United States', type: 'state' },
    '555': { name: 'New York, United States', type: 'state' },
    '504': { name: 'California, United States', type: 'state' },
    '574': { name: 'Texas, United States', type: 'state' }
};

// Helper function to get display name from ID
export const getDisplayNameFromId = (id) => {
    if (!id) return '';
    
    // console.log(`Getting display name for location ID: ${id}`);
    
    // First check common locations for quick lookup
    if (commonLocations[id]) {
        // console.log(`Found in common locations: ${commonLocations[id].name}`);
        return commonLocations[id].name;
    }
    
   
    const knownLocations = {
        '584': 'Johnstown, Pennsylvania, United States',
        '543': 'New York, United States' 
    };
    
    if (knownLocations[id]) {
        // console.log(`Found in known locations: ${knownLocations[id]}`);
        return knownLocations[id];
    }
    
    // Try to get from getLocationById utility
    try {
        const location = getLocationById(id);
        if (location && location.fullName) {
            // console.log(`Found location via getLocationById: ${location.fullName}`);
            return location.fullName;
        }
        
        // If location not found in our database, check if it's a numeric ID
        if (/^\d+$/.test(id)) {
            // console.log(`Trying direct lookup for numeric ID: ${id}`);
            // Try to find the location in the states array directly
            const numericId = Number(id);
            for (const state of states) {
                // Check if it's a state ID
                if (state.locationId === numericId) {
                    const result = `${state.stateName}, ${getCountryName(state.countryAbbrev)}`;
                    // console.log(`Found state via direct lookup: ${result}`);
                    return result;
                }
                
                // Check if it's a city ID
                for (const city of state.cities) {
                    if (city.locationId === numericId) {
                        const result = `${city.name}, ${state.stateName}, ${getCountryName(state.countryAbbrev)}`;
                        // console.log(`Found city via direct lookup: ${result}`);
                        return result;
                    }
                }
            }
            // console.log(`No match found via direct lookup for ID: ${id}`);
        }
    } catch (error) {
        console.error('Error getting location by ID:', error);
    }
    
    // Fallback - return a formatted ID
    // console.log(`Using fallback for ID: ${id}`);
    return `Location (ID: ${id})`;
};

// Helper function to normalize strings for comparison
const normalizeString = (str) => {
    return str.toLowerCase().trim();
};

// Helper function to get full country name from abbreviation
const getCountryName = (countryAbbrev) => {
    const country = countries.find(c => c.code === countryAbbrev);
    return country ? country.name : countryAbbrev;
};

// Helper function to get state details by locationId
const getStateByLocationId = (locationId) => {
    return states.find(state => state.locationId === Number(locationId));
};

// Helper function to get city details by locationId and stateId
const getCityByLocationId = (cityName, stateId) => {
    const state = getStateByLocationId(stateId);
    if (!state) return null;
    return state.cities.find(city => city.name === cityName);
};

// Function to parse location from API response
export const parseLocationFromResponse = (addressData) => {
    if (!addressData) return null;

    try {
        const countryId = addressData.Country?.[0]?.$?.LID;
        const stateId = addressData.State?.[0]?.$?.LID;
        const cityName = addressData.City?.[0];

        // Get state details
        const state = getStateByLocationId(stateId);
        if (!state) return addressData.Location?.[0] || null;

        // Get country name
        const countryName = getCountryName(state.countryAbbrev);

        // If we have a city name from the response, use it directly
        if (cityName) {
            return {
                locationId: state.locationId, // We'll use state's ID since city might not match our database
                type: 'city',
                fullName: `${cityName}, ${state.stateName}, ${countryName}`,
                cityName,
                stateName: state.stateName,
                stateAbbrev: state.stateAbbrev,
                countryName,
                countryAbbrev: state.countryAbbrev,
                postalCode: addressData.PostalCode?.[0],
                rawLocation: addressData.Location?.[0]
            };
        }

        // If no city, return state level location
        return {
            locationId: state.locationId,
            type: 'state',
            fullName: `${state.stateName}, ${countryName}`,
            stateName: state.stateName,
            stateAbbrev: state.stateAbbrev,
            countryName,
            countryAbbrev: state.countryAbbrev,
            postalCode: addressData.PostalCode?.[0],
            rawLocation: addressData.Location?.[0]
        };
    } catch (error) {
        console.error('Error parsing location:', error);
        return addressData.Location?.[0] || null;
    }
};

// Function to check if a string matches the search query
const matchesQuery = (str, query) => {
    // Normalize strings and use more flexible matching
    const normalizedStr = str.toLowerCase().trim();
    const normalizedQuery = query.toLowerCase().trim();
    
    // Try direct includes first (most common case)
    if (normalizedStr.includes(normalizedQuery)) {
        return true;
    }
    
    // Try word-by-word matching for multi-word queries
    const queryWords = normalizedQuery.split(/\s+/);
    if (queryWords.length > 1) {
        // Match if all words in query appear in the string
        return queryWords.every(word => normalizedStr.includes(word));
    }
    
    // For short queries (1-2 characters), be more lenient with matching
    if (normalizedQuery.length <= 2) {
        // Match if string starts with query
        return normalizedStr.startsWith(normalizedQuery);
    }
    
    return false;
};

// Function to get location suggestions based on user input
export const getLocationSuggestions = (searchQuery) => {
    if (!searchQuery) return [];

    const suggestions = [];
    // console.log('Finding location suggestions for:', searchQuery);

    states.forEach(state => {
        const countryName = getCountryName(state.countryAbbrev);

        // Match state name or abbreviation
        if (matchesQuery(state.stateName, searchQuery) ||
            matchesQuery(state.stateAbbrev, searchQuery)) {
            suggestions.push({
                value: `${state.stateName}, ${countryName}`,
                label: `${state.stateName}, ${countryName}`,
                locationId: state.locationId,
                type: 'state',
                countryName,
                countryAbbrev: state.countryAbbrev
            });
        }

        // Match cities - but only if we don't have too many results already
        if (suggestions.length < 20 && state.cities && Array.isArray(state.cities)) {
            state.cities.forEach(city => {
                if (matchesQuery(city.name, searchQuery)) {
                    suggestions.push({
                        value: `${city.name}, ${state.stateName}, ${countryName}`,
                        label: `${city.name}, ${state.stateName}, ${countryName}`,
                        locationId: city.locationId,
                        type: 'city',
                        countryName,
                        countryAbbrev: state.countryAbbrev
                    });
                }
            });
        }
    });

    // If we still have no matches, try a more flexible approach for certain common locations
    if (suggestions.length === 0) {
        // Common location mappings - add more as needed
        const commonLocations = {
            'us': { locationId: '316', name: 'United States', type: 'country' },
            'usa': { locationId: '316', name: 'United States', type: 'country' },
            'united states': { locationId: '316', name: 'United States', type: 'country' },
            'america': { locationId: '316', name: 'United States', type: 'country' },
            'ohio': { locationId: '557', name: 'Ohio, United States', type: 'state' },
            'oh': { locationId: '557', name: 'Ohio, United States', type: 'state' },
            'ny': { locationId: '555', name: 'New York, United States', type: 'state' },
            'new york': { locationId: '555', name: 'New York, United States', type: 'state' },
            'ca': { locationId: '504', name: 'California, United States', type: 'state' },
            'california': { locationId: '504', name: 'California, United States', type: 'state' },
            'tx': { locationId: '574', name: 'Texas, United States', type: 'state' },
            'texas': { locationId: '574', name: 'Texas, United States', type: 'state' }
        };
        
        const normalizedQuery = searchQuery.toLowerCase().trim();
        for (const [key, location] of Object.entries(commonLocations)) {
            if (key.includes(normalizedQuery) || normalizedQuery.includes(key)) {
                suggestions.push({
                    value: location.name,
                    label: location.name,
                    locationId: location.locationId,
                    type: location.type
                });
            }
        }
    }

    // console.log(`Found ${suggestions.length} location suggestions`);
    return suggestions.slice(0, 10); // Limit to 10 results
};

// Function to parse a location string into structured data
export const parseLocation = (locationString) => {
    if (!locationString) return null;

    // Try to match against our location database
    const allSuggestions = getLocationSuggestions(locationString);
    const exactMatch = allSuggestions.find(suggestion =>
        normalizeString(suggestion.value) === normalizeString(locationString)
    );

    if (exactMatch) {
        return {
            locationId: exactMatch.locationId,
            type: exactMatch.type,
            fullName: exactMatch.value,
            countryName: exactMatch.countryName,
            countryAbbrev: exactMatch.countryAbbrev
        };
    }

    // If no exact match, try to parse the string
    const parts = locationString.split(',').map(part => part.trim());

    if (parts.length >= 2) {
        const cityOrState = parts[0];
        const country = parts[parts.length - 1];

        // Try to find matching state
        const matchingState = states.find(state =>
            matchesQuery(state.stateName, cityOrState) ||
            matchesQuery(state.stateAbbrev, cityOrState)
        );

        if (matchingState) {
            const countryName = getCountryName(matchingState.countryAbbrev);
            return {
                locationId: matchingState.locationId,
                type: 'state',
                fullName: `${matchingState.stateName}, ${countryName}`,
                countryName,
                countryAbbrev: matchingState.countryAbbrev
            };
        }

        // Try to find matching city
        for (const state of states) {
            const matchingCity = state.cities.find(city =>
                matchesQuery(city.name, cityOrState)
            );

            if (matchingCity) {
                const countryName = getCountryName(state.countryAbbrev);
                return {
                    locationId: matchingCity.locationId,
                    type: 'city',
                    fullName: `${matchingCity.name}, ${state.stateName}, ${countryName}`,
                    countryName,
                    countryAbbrev: state.countryAbbrev
                };
            }
        }
    }

    // If no structured match found, return the original string
    return {
        locationId: null,
        type: 'unknown',
        fullName: locationString,
        countryName: null,
        countryAbbrev: null
    };
};

// Function to get location details by ID
export const getLocationById = (locationId) => {
    if (!locationId) return null;
    
    // Convert locationId to number for comparison
    const numericId = Number(locationId);
    
    // If conversion failed, return null
    if (isNaN(numericId)) return null;
    
    // console.log(`Looking up location ID: ${locationId} (numeric: ${numericId})`);

    // Search in states
    const matchingState = states.find(state => state.locationId === numericId);
    if (matchingState) {
        // console.log(`Found matching state: ${matchingState.stateName}`);
        const countryName = getCountryName(matchingState.countryAbbrev);
        return {
            locationId: matchingState.locationId,
            type: 'state',
            fullName: `${matchingState.stateName}, ${countryName}`,
            countryName,
            countryAbbrev: matchingState.countryAbbrev
        };
    }

    // Search in cities
    for (const state of states) {
        const matchingCity = state.cities.find(city => city.locationId === numericId);
        if (matchingCity) {
            // console.log(`Found matching city: ${matchingCity.name} in ${state.stateName}`);
            const countryName = getCountryName(state.countryAbbrev);
            return {
                locationId: matchingCity.locationId,
                type: 'city',
                fullName: `${matchingCity.name}, ${state.stateName}, ${countryName}`,
                countryName,
                countryAbbrev: state.countryAbbrev
            };
        }
    }
    
    // console.log(`No matching location found for ID: ${locationId}`);
    return null;
};
